import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label.tsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetActiveGame } from "@/lib/api/games";
import { useGetMenteeNotificationStatus } from "@/lib/api/mentorships";
import { useChannelAvatar } from "@/lib/hooks/conversations/use-channel-avatar.tsx";
import { useChannelPriority } from "@/lib/hooks/conversations/use-channel-priority.ts";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { useGetChannelMentorship } from "@/lib/hooks/conversations/use-get-channel-mentorship";
import { useIsChannelOnline } from "@/lib/hooks/conversations/use-is-channel-online.ts";
import { useActiveCallNotification } from "@/lib/hooks/conversations/useActiveCallNotification";
import { cn } from "@/lib/utils";
import { User } from "@sendbird/chat";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bell,
  BellOff,
  Hourglass,
  Info,
  Loader2,
  PhoneCallIcon,
  Wrench,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { TeenMemoriesModal } from "../conversation-details/teen-memories/teen-memories";
import { CallConfirmationModal } from "../group-call/call-confirmation-modal";
import { GameControlButton } from "./GameControlButton";
import { CustomizeAITraitsButton } from "./ai-traits/ai-traits-button";
import { useFeatureFlagEnabled } from "@posthog/react";
import { HubTrigger } from "@/components/hub/hub-trigger";
import { useAbility } from "@/lib/casl/ability-context";
import { ActivityTrigger } from "./activities/activity-trigger";

interface ChannelHeaderProps {
  isSupervisorChannel: boolean;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;

  isOverridden?: boolean;
  titleOverride?: string | null;
  avatarOverride?: React.ReactNode;
}

export function ChannelHeader({
  isSupervisorChannel,
  leftElement,
  rightElement,
  isOverridden,
  titleOverride,
  avatarOverride,
}: ChannelHeaderProps) {
  const store = useSendbirdStateContext();
  const ability = useAbility();
  const canCreateCalls = ability.can("create", "calls");
  const canUpdateMentorships = ability.can("update", "mentorships");

  const isGameEnabled = useFeatureFlagEnabled("enable-games");
  const isHubEnabled = useFeatureFlagEnabled("enable-hub") || true;
  const channelStore = useChannelContext();
  const channel = channelStore?.currentGroupChannel;

  const queryClient = useQueryClient();
  const [priorityMode, setPriorityMode] = useState(false);

  const { data: priority } = useChannelPriority(channel!);

  const channelTitle = useMemo(() => {
    if (titleOverride) return titleOverride;

    const membersNamesExcludingMe =
      channel?.members
        ?.filter((member) => member.userId !== store.config.userId)
        ?.map((member) => member.nickname || member.userId)
        ?.join(", ") || "";

    return membersNamesExcludingMe.length < 30
      ? membersNamesExcludingMe.length === 0
        ? "(No members)"
        : membersNamesExcludingMe
      : `${membersNamesExcludingMe.slice(0, 30)}...`;
  }, [store.config.userId, channel, titleOverride]);

  const isOnline = useIsChannelOnline(
    channel ?? undefined,
    store.config.userId,
  );

  const channelAvatar = useChannelAvatar(
    channel ?? undefined,
    store.config.userId,
    !!priority,
    isOnline,
    false,
  );

  const lastOnlineTime = useMemo(() => {
    if (!channel) return null;

    const { members } = channel;
    const membersExcludingMe = members.filter(
      (member) => member.userId !== store.config.userId,
    );

    if (isOnline) {
      return "Online";
    }

    // check if all members are offline
    if (
      membersExcludingMe.every(
        (member) => member.connectionStatus === "offline",
      )
    ) {
      // find the last online member
      const lastOnlineMember: User = membersExcludingMe.reduce(
        (prev, current) => {
          if (!prev.lastSeenAt) return current;
          if (!current.lastSeenAt) return prev;

          return prev.lastSeenAt > current.lastSeenAt ? prev : current;
        },
        {} as User,
      );

      if (!lastOnlineMember.lastSeenAt || lastOnlineMember.lastSeenAt === -1)
        return "Offline";

      // return the last online time
      return `Last seen ${new Date(
        lastOnlineMember.lastSeenAt,
      ).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`;
    }

    // Check if all members are online
    if (
      membersExcludingMe.every((member) => member.connectionStatus === "online")
    ) {
      return "Online";
    }

    return "Some members are online";
  }, [channel, store.config.userId, isOnline]);

  const togglePriorityMode = async (newPriorityMode: boolean) => {
    if (!channel) return;

    setPriorityMode(newPriorityMode);
    await channel?.updateMetaData(
      {
        priority_mode: newPriorityMode ? "true" : "false",
      },
      true,
    );
    queryClient.invalidateQueries({
      queryKey: ["channel-priority", channel.url],
    });
  };

  useEffect(() => {
    (async () => {
      const priorityModeMeta = await channel?.getMetaData(["priority_mode"]);
      setPriorityMode(priorityModeMeta?.priority_mode === "true");
    })();
  }, [channel]);

  const membersExcludingMe = channel?.members?.filter(
    (member) => member.userId !== store.config.userId,
  );

  const setCurrentTeenId = useCurrentConversation(
    (state) => state.setCurrentTeenId,
  );

  const menteeId = membersExcludingMe?.[0]?.userId as string; // @todo - handle multiple members

  useEffect(() => {
    if (!menteeId) return;

    setCurrentTeenId(menteeId);
  }, [menteeId, setCurrentTeenId]);

  // Get mentorship and active game state
  const { mentorship } = useGetChannelMentorship();
  const { data: activeGame } = useGetActiveGame();

  return (
    <div className="flex w-full flex-row items-center gap-2 border-b px-4">
      {leftElement}

      {avatarOverride ?? channelAvatar}

      <div
        className={cn("flex flex-1 flex-row items-center px-2 py-2", {
          "border-r": !isSupervisorChannel,
        })}
      >
        <div className="flex flex-1 flex-col">
          <span className="text-base font-bold text-black">{channelTitle}</span>

          <div className="flex flex-row items-center gap-2">
            <span className="mt-1 inline-block text-xs font-normal text-zinc-400">
              {lastOnlineTime}
            </span>
          </div>
        </div>
      </div>

      {/* Game Control Button */}
      {!isSupervisorChannel && !isOverridden && mentorship && isGameEnabled && (
        <GameControlButton
          mentorshipId={mentorship.id}
          sendbirdChannelUrl={channel?.url || ""}
          activeGame={activeGame || null}
        />
      )}

      {/* Engagement (activity) trigger */}
      {!isSupervisorChannel && !isOverridden && mentorship && (
        <ActivityTrigger
          mentorshipId={mentorship.id}
          teenName={mentorship.mentee?.name?.split(" ")[0] || "Teen"}
        />
      )}

      {/* Right side tools popover */}
      {!isSupervisorChannel && !isOverridden && (
        <>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="ml-2">
                <Wrench className="mr-2 h-4 w-4" />
                Tools
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-64 rounded-2xl p-0 shadow-lg"
            >
              <div className="flex flex-col py-2">
                <div className="flex items-center px-4 py-2">
                  <Switch
                    id="priority-mode"
                    checked={priorityMode}
                    onCheckedChange={async (checked) => {
                      await togglePriorityMode(checked);
                    }}
                  />
                  <Label
                    htmlFor="priority-mode"
                    className="ml-3 cursor-pointer text-base font-normal text-zinc-500"
                  >
                    Priority mode
                  </Label>
                </div>
                <Separator className="my-1" />
                {canCreateCalls && (
                  <>
                    <CallButton otherUserId={menteeId} />
                    <Separator className="my-1" />
                  </>
                )}
                <ViewMemoriesButton menteeId={menteeId} />
                {canUpdateMentorships && (
                  <>
                    <Separator className="my-1" />
                    <CustomizeAITraitsButton menteeId={menteeId} />
                  </>
                )}
                <Separator className="my-1" />
                <div className="flex cursor-not-allowed select-none items-center px-4 py-2 opacity-50">
                  <AlertTriangle className="mr-3 h-5 w-5 text-zinc-400" />
                  <span className="text-base text-zinc-400">Trigger log</span>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          {isHubEnabled && <HubTrigger />}
        </>
      )}
      {rightElement}
    </div>
  );
}

function CallButton({ otherUserId }: { otherUserId: string }) {
  useActiveCallNotification(otherUserId);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className="flex cursor-pointer items-center px-4 py-2 transition-colors hover:bg-zinc-100"
        onClick={() => {
          setOpen(true);
        }}
      >
        <PhoneCallIcon className="mr-3 h-5 w-5 text-zinc-500" />
        <span className="text-base text-zinc-700">Call teen</span>
      </div>
      <CallConfirmationModal
        otherUserId={otherUserId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export function ViewMemoriesButton({ menteeId }: { menteeId: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className="flex cursor-pointer items-center px-4 py-2 transition-colors hover:bg-zinc-100"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Hourglass className="mr-3 h-5 w-5 text-zinc-500" />
        <span className="text-base text-zinc-700">View memories</span>
      </div>
      <TeenMemoriesModal
        teenId={menteeId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export function NotificationStatusIcon({
  userId,
  className,
}: {
  userId?: string;
  className?: string;
}) {
  const { data: notificationData, isLoading } = useGetMenteeNotificationStatus(
    userId || "",
  );

  if (!userId) return null;

  const hasNotificationsEnabled = notificationData?.data?.pushOptedIn ?? false;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2">
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
              ) : hasNotificationsEnabled ? (
                <Bell className="h-5 w-5 text-green-600" />
              ) : (
                <BellOff className="h-5 w-5 text-gray-400" />
              )}
              <span className="text-sm text-zinc-500">
                {isLoading
                  ? "Loading..."
                  : hasNotificationsEnabled
                    ? "Notifications On"
                    : "Notifications Off"}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {hasNotificationsEnabled
                ? "User has notifications enabled"
                : "User has notifications disabled"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog>
        <DialogTrigger asChild>
          <button className="text-zinc-400 transition-colors hover:text-zinc-600">
            <Info className="h-5 w-5" />
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Notification Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-zinc-600">
            <p>
              This indicator shows whether the user has notifications enabled or
              disabled for this conversation.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-green-600" />
                <span className="font-medium">Notifications On:</span>
                <span>User will receive push notifications and alerts</span>
              </div>
              <div className="flex items-center gap-2">
                <BellOff className="h-4 w-4 text-gray-400" />
                <span className="font-medium">Notifications Off:</span>
                <span>
                  User has disabled notifications for this conversation
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
